import React, { useEffect, useMemo, useState } from 'react'
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Input, Switch } from '../ui'
import Button from '../ui/Button/Button'

export type TipoUnidadEmpaque = 'bolsa' | 'caja' | 'wacal'

export interface UnidadEmpaqueItem {
  tipo: TipoUnidadEmpaque
  cantidad: number
}

interface EmpaqueUnidadesModalProps {
  isOpen: boolean
  isSubmitting?: boolean
  onClose: () => void
  onConfirm: (unidadesEmpaque: UnidadEmpaqueItem[]) => void
}

const TIPOS_EMPAQUE: Array<{ tipo: TipoUnidadEmpaque; label: string }> = [
  { tipo: 'bolsa', label: 'Bolsa' },
  { tipo: 'caja', label: 'Caja' },
  { tipo: 'wacal', label: 'Wacal' }
]

const INITIAL_SELECTED: Record<TipoUnidadEmpaque, boolean> = {
  bolsa: false,
  caja: false,
  wacal: false
}

const INITIAL_CANTIDADES: Record<TipoUnidadEmpaque, number> = {
  bolsa: 1,
  caja: 1,
  wacal: 1
}

const EmpaqueUnidadesModal: React.FC<EmpaqueUnidadesModalProps> = ({
  isOpen,
  isSubmitting = false,
  onClose,
  onConfirm
}) => {
  const [selected, setSelected] = useState(INITIAL_SELECTED)
  const [cantidades, setCantidades] = useState(INITIAL_CANTIDADES)

  useEffect(() => {
    if (isOpen) {
      setSelected(INITIAL_SELECTED)
      setCantidades(INITIAL_CANTIDADES)
    }
  }, [isOpen])

  const hasAtLeastOneSelected = useMemo(
    () =>
      TIPOS_EMPAQUE.some(({ tipo }) => selected[tipo] && Math.max(0, Number(cantidades[tipo]) || 0) >= 1),
    [cantidades, selected]
  )

  const unidadesEmpaque = useMemo(
    (): UnidadEmpaqueItem[] =>
      TIPOS_EMPAQUE.map(({ tipo }) => ({
        tipo,
        cantidad: selected[tipo] ? Math.max(1, Number(cantidades[tipo]) || 1) : 0
      })),
    [cantidades, selected]
  )

  if (!isOpen) return null

  const handleToggle = (tipo: TipoUnidadEmpaque, checked: boolean) => {
    setSelected((current) => ({ ...current, [tipo]: checked }))
    if (checked) {
      setCantidades((current) => ({
        ...current,
        [tipo]: Math.max(1, Number(current[tipo]) || 1)
      }))
    }
  }

  const handleCantidadChange = (tipo: TipoUnidadEmpaque, value: string) => {
    const parsed = Number.parseInt(value, 10)
    setCantidades((current) => ({
      ...current,
      [tipo]: Number.isNaN(parsed) ? 0 : Math.max(0, parsed)
    }))
  }

  const handleConfirm = () => {
    if (!hasAtLeastOneSelected || isSubmitting) return
    onConfirm(unidadesEmpaque)
  }

  return (
    <div className="fixed inset-0 z-50 flex h-full w-full items-center justify-center overflow-y-auto bg-gray-600 bg-opacity-50">
      <div
        className="relative m-4 w-full max-w-md rounded-lg bg-white p-8 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="empaque-unidades-title"
      >
        <div className="mb-6 flex items-center justify-between">
          <h2 id="empaque-unidades-title" className="text-2xl font-bold text-custom-blue">
            Unidades de empaque
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Cerrar modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4 py-2">
          <div className="flex justify-center">
            <CheckCircleIcon className="h-16 w-16 text-green-600" aria-hidden="true" />
          </div>
          <p className="text-center text-sm text-gray-500">
            Selecciona las unidades de empaque de este pedido. Debes marcar al menos una opción.
          </p>

          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            {TIPOS_EMPAQUE.map(({ tipo, label }) => (
              <div
                key={tipo}
                className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <Switch
                  checked={selected[tipo]}
                  onChange={(checked) => handleToggle(tipo, checked)}
                  label={label}
                  disabled={isSubmitting}
                />
                <div className="w-full sm:w-28">
                  <Input
                    type="number"
                    min={1}
                    value={selected[tipo] ? String(cantidades[tipo]) : '0'}
                    onChange={(event) => handleCantidadChange(tipo, event.target.value)}
                    disabled={!selected[tipo] || isSubmitting}
                    aria-label={`Cantidad de ${label}`}
                  />
                </div>
              </div>
            ))}
          </div>

          {!hasAtLeastOneSelected && (
            <p className="text-sm text-amber-700" role="status">
              Selecciona al menos una unidad de empaque para continuar.
            </p>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="success"
            onClick={handleConfirm}
            disabled={!hasAtLeastOneSelected || isSubmitting}
            isLoading={isSubmitting}
          >
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  )
}

export default EmpaqueUnidadesModal
